import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Loader2, Mail, User as UserIcon } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { RouteError } from '@/components/common/route-error'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/ui/form-field'
import { LocationCombobox } from '@/components/ui/location-combobox'
import { SuggestCombobox } from '@/components/ui/suggest-combobox'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { countryCodeOf, suggestCountries } from '@/lib/countries'
import { composeLocation, splitLocation } from '@/lib/location'
import { z } from 'zod'

import {
  profileQuery,
  profileUpdateFieldsSchema,
  useUpdateProfileMutation,
  type Profile,
  type ProfileUpdateInput,
} from '@/api/profile'
import { ApiError } from '@/api/errors'

/**
 * 表单用 Country + City 两步填写，接口只认一个 `location` 字符串
 * （见 @/lib/location），所以这里把 location 换掉再提交时合成回去。
 */
const profileFormSchema = profileUpdateFieldsSchema
  .omit({ location: true })
  .extend({
    country: z.string().trim().min(1, 'Country is required').max(120),
    city: z.string().trim().max(120).optional(),
  })
type ProfileForm = z.infer<typeof profileFormSchema>

/**
 * /profile — view + edit the LoopCV-backed candidate profile (P2.8 FE).
 *
 * Form strategy:
 *   - react-hook-form (matches loop-form-dialog.tsx, the only other form
 *     of comparable shape in the codebase).
 *   - zodResolver against profileUpdateInputSchema; everything optional
 *     because the backend ProfileUpdate uses pointer types to distinguish
 *     "leave unchanged" from "set to zero".
 *   - Dirty-field tracking: we read formState.dirtyFields on submit and
 *     send ONLY the keys the user actually edited. This mirrors the
 *     backend's omitempty semantics and prevents accidentally clobbering
 *     a field that was untouched.
 *
 * Notifications section:
 *   - The backend Profile shape doesn't currently surface notificationSettings
 *     on GET (Agent A owns that struct and we don't extend it here), so the
 *     toggle starts un-dirty / defaults to OFF in the UI per Round 10 policy
 *     ("don't let LoopCV email our users; surface in-app instead"). The
 *     user toggling it on WILL persist to LoopCV via PUT; we just can't
 *     reflect a pre-existing upstream value here yet.
 *   - TODO(P2.8 follow-up): when Profile gains a notificationSettings
 *     field, hydrate the default from server state.
 */
export const Route = createFileRoute('/_authed/profile')({
  loader: ({ context }) => context.queryClient.ensureQueryData(profileQuery()),
  component: ProfilePage,
  errorComponent: RouteError,
})

function ProfilePage() {
  const { data: profile } = useSuspenseQuery(profileQuery())
  const update = useUpdateProfileMutation()

  // Defaults mirror server state so dirty tracking is meaningful (a
  // field is "dirty" only if the user actually changed it away from
  // the current server value).
  const defaults = useMemo<ProfileForm>(() => {
    const { country, city } = splitLocation(profile.location)
    return {
      firstName: profile.firstName,
      lastName: profile.lastName,
      country,
      city,
      email: profile.email,
      phoneCountryCallingCode: profile.phoneData?.countryCallingCode ?? '',
      phoneNationalNumber: profile.phoneData?.formatNational ?? '',
      sendEmailWithoutCV: profile.sendEmailWithoutCV,
      enableEmailsApplications: profile.enableEmailsApplications,
      automaticallyAnswerQuestions: profile.automaticallyAnswerQuestions,
      // Notifications: see route-level comment — defaults to OFF because
      // we don't yet read this back from GET.
      notificationSettings: {
        questionNotificationToUsers: {
          enabled: false,
          channel: 'email',
        },
      },
    }
  }, [profile])

  const form = useForm<ProfileForm>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: defaults,
    // values: keep the form in sync when a background refetch lands
    // (e.g. after an apply ticks a quota counter); skipping this would
    // freeze the form on the first snapshot.
    values: defaults,
  })

  // 国家定了才给城市补全限定范围。
  const cityCountryCode = countryCodeOf(form.watch('country'))

  const onSubmit = (values: ProfileForm) => {
    // Dirty-field tracking: only send keys the user actually edited.
    // RHF's dirtyFields is a sparse object whose keys mirror the form
    // tree; we walk the top-level once to build the payload.
    const dirty = form.formState.dirtyFields
    const payload: ProfileUpdateInput = {}
    if (dirty.firstName) payload.firstName = values.firstName
    if (dirty.lastName) payload.lastName = values.lastName
    if (dirty.country || dirty.city) {
      payload.location = composeLocation(values.country, values.city ?? '')
    }
    if (dirty.email) payload.email = values.email
    if (dirty.phoneCountryCallingCode || dirty.phoneNationalNumber) {
      payload.phoneCountryCallingCode = values.phoneCountryCallingCode
      payload.phoneNationalNumber = values.phoneNationalNumber
    }
    if (dirty.sendEmailWithoutCV) payload.sendEmailWithoutCV = values.sendEmailWithoutCV
    if (dirty.enableEmailsApplications)
      payload.enableEmailsApplications = values.enableEmailsApplications
    if (dirty.automaticallyAnswerQuestions)
      payload.automaticallyAnswerQuestions = values.automaticallyAnswerQuestions
    // notificationSettings is a nested object; if any nested key is
    // dirty, send the whole sub-tree (the backend's pointer fields
    // handle partial sub-objects).
    if (dirty.notificationSettings) {
      payload.notificationSettings = values.notificationSettings
    }

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save')
      return
    }

    update.mutate(payload, {
      onSuccess: (fresh: Profile) => {
        toast.success('Profile updated')
        // Reset the dirty flags to the new server state.
        const { country, city } = splitLocation(fresh.location)
        form.reset({
          ...values,
          firstName: fresh.firstName,
          lastName: fresh.lastName,
          country,
          city,
          email: fresh.email,
          phoneCountryCallingCode: fresh.phoneData?.countryCallingCode ?? '',
          phoneNationalNumber: fresh.phoneData?.formatNational ?? '',
          sendEmailWithoutCV: fresh.sendEmailWithoutCV,
          enableEmailsApplications: fresh.enableEmailsApplications,
          automaticallyAnswerQuestions: fresh.automaticallyAnswerQuestions,
        })
      },
      onError: (err) => {
        if (err instanceof ApiError && err.fields) {
          for (const [field, message] of Object.entries(err.fields)) {
            // Only assign field errors that match a known form key.
            const known: (keyof ProfileUpdateInput)[] = [
              'firstName',
              'lastName',
              'location',
              'email',
              'phoneCountryCallingCode',
              'phoneNationalNumber',
            ]
            if (known.includes(field as keyof ProfileUpdateInput)) {
              form.setError(field === 'location' ? 'country' : (field as keyof ProfileForm), {
                message,
              })
            }
          }
          toast.error(err.message || 'Some fields were rejected')
          return
        }
        if (err instanceof ApiError && err.code === 'forbidden') {
          toast.error(err.message || 'This change requires a higher plan')
          return
        }
        toast.error(err instanceof Error ? err.message : 'Save failed')
      },
    })
  }

  const errors = form.formState.errors
  const dirty = form.formState.isDirty

  return (
    <PageContainer>
      <PageHeader
        title="Profile"
        description="Personal details, apply preferences, and notification settings for your LoopCV-backed candidate profile."
      />

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-[1.5fr_1fr]"
        noValidate
      >
        <div className="flex flex-col gap-6">
          {/* Personal info */}
          <Section
            title="Personal"
            description="Used on outbound applications and the optional cover letter header."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="First name" htmlFor="firstName" error={errors.firstName?.message}>
                <Input id="firstName" aria-invalid={!!errors.firstName} {...form.register('firstName')} />
              </FormField>
              <FormField label="Last name" htmlFor="lastName" error={errors.lastName?.message}>
                <Input id="lastName" aria-invalid={!!errors.lastName} {...form.register('lastName')} />
              </FormField>
              <FormField label="Country" htmlFor="country" error={errors.country?.message}>
                <Controller
                  name="country"
                  control={form.control}
                  render={({ field }) => (
                    <SuggestCombobox
                      id="country"
                      value={field.value}
                      onChange={field.onChange}
                      suggestions={{
                        items: suggestCountries(field.value),
                        isLoading: false,
                        isUnavailable: false,
                      }}
                      itemKey={(country) => country.code}
                      selectValue={(country) => country.name}
                      renderItem={(country) => country.name}
                      placeholder="e.g. Singapore"
                      ariaInvalid={!!errors.country}
                    />
                  )}
                />
              </FormField>
              {cityCountryCode && (
                <FormField
                  label="City (optional)"
                  htmlFor="city"
                  hint="Skip it for city-states like Singapore."
                  error={errors.city?.message}
                >
                  <Controller
                    name="city"
                    control={form.control}
                    render={({ field }) => (
                      <LocationCombobox
                        id="city"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        country={cityCountryCode}
                        selectValue={(item) => item.name}
                        placeholder="e.g. Berlin"
                        ariaInvalid={!!errors.city}
                      />
                    )}
                  />
                </FormField>
              )}
              <FormField label="Email" htmlFor="email" error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  {...form.register('email')}
                />
              </FormField>
              <FormField
                label="Calling code"
                htmlFor="phoneCountryCallingCode"
                error={errors.phoneCountryCallingCode?.message}
              >
                <Input
                  id="phoneCountryCallingCode"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-country-code"
                  placeholder="+65"
                  aria-invalid={!!errors.phoneCountryCallingCode}
                  {...form.register('phoneCountryCallingCode')}
                />
              </FormField>
              <FormField
                label="Phone number"
                htmlFor="phoneNationalNumber"
                error={errors.phoneNationalNumber?.message}
              >
                <Input
                  id="phoneNationalNumber"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="8123 4567"
                  aria-invalid={!!errors.phoneNationalNumber}
                  {...form.register('phoneNationalNumber')}
                />
              </FormField>
            </div>
          </Section>

          {/* Apply preferences */}
          <Section
            title="Apply preferences"
            description="How LoopCV submits applications on your behalf."
          >
            <div className="flex flex-col gap-3">
              <Controller
                name="automaticallyAnswerQuestions"
                control={form.control}
                render={({ field }) => (
                  <ToggleRow
                    label="Auto-answer screening questions"
                    description="LoopCV's bot uses AI to fill out custom screening questions. Requires a higher plan — we'll surface a clear error if your plan doesn't allow it."
                    checked={!!field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="sendEmailWithoutCV"
                control={form.control}
                render={({ field }) => (
                  <ToggleRow
                    label="Send emails without CV attached"
                    description="Some employers reject attachments at the gateway. Turn this on if you'd rather send a CV-less plain email and link to your resume from the body."
                    checked={!!field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="enableEmailsApplications"
                control={form.control}
                render={({ field }) => (
                  <ToggleRow
                    label="Enable email-based applications"
                    description="Lets LoopCV apply via the partner SMTP path when a job posting exposes an apply-by-email address. Off until SMTP is configured for your account."
                    checked={!!field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </Section>

          {/* Notifications */}
          <Section
            title="Notifications"
            description="Where pending-question reminders are delivered."
          >
            <Controller
              name="notificationSettings.questionNotificationToUsers.enabled"
              control={form.control}
              render={({ field }) => (
                <ToggleRow
                  label="Email me when a match needs answers"
                  description={
                    'Off by default — we surface pending-question matches inside the app instead of routing LoopCV email to your inbox. Turn this on if you want both.'
                  }
                  checked={!!field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Section>

          <div className="flex items-center justify-end gap-3">
            {dirty ? (
              <span className="text-xs text-muted">You have unsaved changes</span>
            ) : null}
            <Button type="submit" disabled={!dirty || update.isPending}>
              {update.isPending ? <Loader2 className="animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </div>

        {/* Read-only account info */}
        <aside className="flex flex-col gap-4">
          <AccountInfoCard profile={profile} />
        </aside>
      </form>
    </PageContainer>
  )
}

/* ----- presentational pieces (kept local; not reused yet) ----------- */

interface SectionProps {
  title: string
  description?: string
  children: React.ReactNode
}

function Section({ title, description, children }: SectionProps) {
  return (
    <section className="rounded-[var(--radius-card)] border border-divider bg-surface-base p-6 shadow-card">
      <header className="mb-4 space-y-1">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {description ? <p className="text-xs text-muted">{description}</p> : null}
      </header>
      {children}
    </section>
  )
}

interface ToggleRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (next: boolean) => void
}

/**
 * Checkbox-as-toggle row. We reuse the existing Checkbox primitive
 * (mockup uses checkboxes throughout — no Switch primitive lives in
 * the codebase yet, and introducing one here would diverge from the
 * loop-form Apply step's visual language).
 */
function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border p-3 transition-colors',
        checked
          ? 'border-primary bg-info-bg/40'
          : 'border-divider bg-surface-base hover:bg-surface-sunken',
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(c) => onChange(c === true)}
        className="mt-0.5"
      />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-ink">{label}</p>
        {description ? (
          <p className="text-xs leading-relaxed text-muted">{description}</p>
        ) : null}
      </div>
    </label>
  )
}

interface AccountInfoCardProps {
  profile: Profile
}

/**
 * Account info panel — server-set fields the user can't edit but should
 * be able to inspect (plan, quota counters, outbound-email address).
 *
 * Quota convention: `-1` is the unlimited sentinel (Profile docstring);
 * we render "Unlimited" rather than the literal number.
 */
function AccountInfoCard({ profile }: AccountInfoCardProps) {
  return (
    <section className="rounded-[var(--radius-card)] border border-divider bg-surface-base p-6 shadow-card">
      <header className="mb-4 space-y-1">
        <h2 className="text-base font-semibold text-ink">Account info</h2>
        <p className="text-xs text-muted">
          Managed by LoopCV. Contact support to change your plan.
        </p>
      </header>
      <dl className="space-y-3 text-sm">
        <Row label="Plan">
          <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-info-bg px-2 py-0.5 text-xs font-semibold text-info-fg">
            <UserIcon className="size-3" />
            Plan {profile.pricingPlan}
          </span>
        </Row>
        <Row label="Applications remaining">{quotaLabel(profile.numRemainingApplications)}</Row>
        <Row label="CV reports remaining">{quotaLabel(profile.numRemainingCvReports)}</Row>
        <Row label="Sending address">
          <span className="inline-flex items-center gap-1.5 text-xs text-body">
            <Mail className="size-3 text-muted" />
            {profile.platformEmail || '—'}
          </span>
        </Row>
        <Row label="Active loops">{profile.loopCount}</Row>
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className="text-sm font-medium text-ink">{children}</dd>
    </div>
  )
}

function quotaLabel(n: number): string {
  return n === -1 ? 'Unlimited' : String(n)
}
